<template>
    <div
        v-if="visible"
        :class="[
            'alert-message-container',
            `is-${type}`,
            { 'is-inverted': inverted, 'is-borderless': borderless },
            'mt-3',
        ]"
        role="status"
        :aria-live="type === 'error' ? 'assertive' : 'polite'"
        aria-atomic="true"
    >
        <div v-if="showIcon" class="icon main" aria-hidden="true">
            <!-- icon: use sprite symbols -->
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <use v-if="type !== 'success'" xlink:href="#icon-alert-triangle"></use>
                <use v-else xlink:href="#icon-check"></use>
            </svg>
        </div>
        <div class="alert-message-content">
            <p v-if="header" class="alert-header">{{ header }}</p>
            <p class="alert-message">{{ message }}</p>
        </div>
        <a class="alert-close" href="#" title="Close" @click.prevent="close">
            <span class="icon" aria-hidden="true">
                <svg width="15px" height="15px">
                    <use xlink:href="#icon-close"></use>
                </svg>
            </span>
            <span class="sr-only">Close</span>
        </a>
    </div>
</template>

<script>
    import { defineComponent, onBeforeUnmount, onMounted, ref, watch } from 'vue';

    export default defineComponent({
        name: 'AlertMessage',
        emits: ['close'],
        props: {
            autoHide: { default: true, type: Boolean },
            borderless: { default: false, type: Boolean },
            duration: { default: 6000, type: Number },
            header: { default: 'Heads up!', type: String },
            inverted: { default: false, type: Boolean },
            message: { default: 'Alert message default', type: String },
            showIcon: { default: true, type: Boolean },
            type: {
                default: 'info',
                type: String,
                validator: v => ['error', 'info', 'primary', 'success', 'warning'].includes(v),
            },
        },
        setup(props, { emit }) {
            const visible = ref(true);
            let timer = null;

            const clearTimer = () => {
                if (timer) {
                    clearTimeout(timer);
                    timer = null;
                }
            };

            const startTimer = () => {
                clearTimer();
                if (props.autoHide && props.duration > 0) {
                    timer = setTimeout(() => {
                        visible.value = false;
                        emit('close');
                    }, props.duration);
                }
            };

            const close = () => {
                clearTimer();
                visible.value = false;
                emit('close');
            };

            onMounted(() => {
                visible.value = true;
                startTimer();
            });

            onBeforeUnmount(() => {
                clearTimer();
            });

            watch(
                () => props.message,
                () => {
                    if (!props.message) return;
                    visible.value = true;
                    startTimer();
                }
            );

            return { close, visible };
        },
    });
</script>

<style scoped>
    .alert-message-container {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        padding: var(--space-075) var(--space-1);
        border-radius: var(--radius-radius-md);
        color: var(--color-text-on-color);
        background-color: var(--color-bg); /* default background when not inverted */
        border: 1px solid var(--color-border); /* default border when not inverted */

        /* NON-INVERTED (inverted = false) - background is --color-bg for all types
           but header/body and border colors vary by type */
        &.is-info {
            border-color: var(--color-border);
            .alert-header {
                color: var(--color-text);
            }
            .alert-message {
                color: var(--color-text-muted);
            }

            .icon svg,
            .alert-close svg {
                color: var(--color-text);
                fill: currentColor;
                stroke: currentColor;
            }
        }

        &.is-warning {
            border-color: var(--color-border-primary);
            .alert-header {
                color: var(--color-text-primary);
            }
            .alert-message {
                color: var(--color-text-primary);
            }

            .icon svg,
            .alert-close svg {
                color: var(--color-text-primary);
                fill: currentColor;
                stroke: currentColor;
            }
        }

        &.is-primary {
            border-color: var(--color-border-primary);
            .alert-header {
                color: var(--color-text-primary);
            }
            .alert-message {
                color: var(--color-text-primary);
            }

            .icon svg,
            .alert-close svg {
                color: var(--color-text-primary);
                fill: currentColor;
                stroke: currentColor;
            }
        }

        &.is-error {
            border-color: var(--color-border-error);
            .alert-header {
                color: var(--color-text-error);
            }
            .alert-message {
                color: var(--color-text-error);
            }

            .icon svg,
            .alert-close svg {
                color: var(--color-text-error);
                fill: currentColor;
                stroke: currentColor;
            }
        }

        &.is-success {
            border-color: var(--color-border-success);
            .alert-header {
                color: var(--color-text-success, var(--color-icon-success));
            }
            .alert-message {
                color: var(--color-text-success, var(--color-icon-success));
            }

            .icon svg,
            .alert-close svg {
                color: var(--color-text-success, var(--color-icon-success));
                fill: currentColor;
                stroke: currentColor;
            }
        }

        /* INVERTED styles - when .is-inverted is present, override background, border and text colors per type */
        &.is-inverted {
            /* default fallback for inverted containers */
            background-color: var(--color-bg-info);
            color: var(--color-text);

            &.is-info {
                background-color: var(--color-bg-info);
                border-color: var(--color-border);
                .alert-header {
                    color: var(--color-text);
                }
                .alert-message {
                    color: var(--color-text);
                }

                .icon svg,
                .alert-close svg {
                    color: var(--color-text);
                    fill: currentColor;
                    stroke: currentColor;
                }
            }

            &.is-warning {
                background-color: var(--color-bg-cream);
                border-color: var(--color-bg-cream);
                .alert-header {
                    color: var(--color-text-primary);
                }
                .alert-message {
                    color: var(--color-text-primary);
                }

                .icon svg,
                .alert-close svg {
                    color: var(--color-text-primary);
                    fill: currentColor;
                    stroke: currentColor;
                }
            }

            &.is-primary {
                background-color: var(--color-bg-primary);
                border-color: var(--color-bg-primary);
                .alert-header {
                    color: var(--color-text-error-inverted);
                }
                .alert-message {
                    color: var(--color-text-error-inverted);
                }

                .icon svg,
                .alert-close svg {
                    color: var(--color-text-error-inverted);
                    fill: currentColor;
                    stroke: currentColor;
                }
            }

            &.is-error {
                background-color: var(--color-bg-error-inverted);
                border-color: var(--color-border-error);
                .alert-header {
                    color: var(--color-text-error-inverted);
                }
                .alert-message {
                    color: var(--color-text-error-inverted);
                }

                .icon svg,
                .alert-close svg {
                    color: var(--color-text-error-inverted);
                    fill: currentColor;
                    stroke: currentColor;
                }
            }

            &.is-success {
                background-color: var(--color-bg-success);
                border-color: var(--color-border-success);
                .alert-header {
                    color: var(--color-text-error-inverted);
                }
                .alert-message {
                    color: var(--color-text-error-inverted);
                }

                .icon svg,
                .alert-close svg {
                    color: var(--color-text-error-inverted);
                    fill: currentColor;
                    stroke: currentColor;
                }
            }
        }

        .alert-close {
            display: inline-flex;
            align-self: baseline;
            align-items: center;
            gap: 0.5rem;
            background: transparent;
            border: none;
            color: inherit;
            text-decoration: none;
            cursor: pointer;
        }

        .icon {
            flex: 0 0 auto;
            display: flex;
            align-items: center;
            justify-content: center;
            align-self: start;

            &.main {
                margin-right: 0.75rem;
            }
        }

        .alert-message-content {
            flex: 1 1 auto;
            margin: 0;
            p {
                margin: 0;
            }

            .alert-header {
                font-weight: var(--weight-medium);
                font-size: var(--typo-size-alert);
                line-height: var(--typo-line-height-line-height-base);
                letter-spacing: var(--typo-letter-spacing-letter-spacing-base);
            }
            .alert-message {
                font-weight: var(--weight-normal);
                font-size: var(--typo-size-alert);
                line-height: var(--typo-line-height-line-height-base);
                letter-spacing: var(--typo-letter-spacing-letter-spacing-base);
            }
        }
    }
</style>
